/**
 * useSessionTimeout
 *
 * Drives the session timeout warning for internal staff and silent expiry for
 * guides/customers. Reads the platform RS256 access token's `exp` claim to
 * determine when to warn and when to hard-expire the session.
 *
 * Behaviour per user type (MVP 1.15 V2 Epic 4, SCRUM-366):
 *   - internal staff ('staff' | 'admin'): warning modal at 5 min remaining,
 *     hard expiry clears tokens + redirects to /login. No extend option.
 *   - guides / customers: silent expiry only — no modal. The Token Refresh
 *     Interceptor (SCRUM-355) handles proactive refresh; this hook fires only
 *     if the token reaches hard expiry without being renewed.
 *
 * This hook does NOT implement refresh logic — that lives in tokenRefreshInterceptor.js.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  decodeJwtPayload,
  clearTokens,
  redirectToLogin,
} from '../services/tokenRefreshInterceptor';

/** Seconds before hard expiry at which the warning modal is shown for staff. */
export const SESSION_WARNING_SECONDS = 300; // 5 minutes

/**
 * Returns 'internal' for staff/admin user types, 'other' for guides/customers.
 * @param {Object|null} payload - decoded JWT payload
 * @returns {'internal'|'other'}
 */
export function resolveUserCategory(payload) {
  if (!payload) { return 'other'; }
  const type = payload.user_type || payload.role || '';
  return (type === 'staff' || type === 'admin') ? 'internal' : 'other';
}

/**
 * @returns {{
 *   showWarning: boolean,
 *   userCategory: 'internal'|'other',
 * }}
 */
export function useSessionTimeout() {
  const { accessToken } = useAuth();
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    if (!accessToken) {
      setShowWarning(false);
      return;
    }

    const payload = decodeJwtPayload(accessToken);
    if (!payload || typeof payload.exp !== 'number') {
      setShowWarning(false);
      return;
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    const secondsUntilExpiry = payload.exp - nowSeconds;
    const userCategory = resolveUserCategory(payload);

    if (secondsUntilExpiry <= 0) {
      handleExpiry();
      return;
    }

    const timers = [];

    if (userCategory === 'internal') {
      // Warning timer — fires when SESSION_WARNING_SECONDS remain
      const warningDelay = Math.max(0, (secondsUntilExpiry - SESSION_WARNING_SECONDS) * 1000);
      timers.push(
        setTimeout(() => setShowWarning(true), warningDelay)
      );

      // Hard expiry timer
      timers.push(
        setTimeout(handleExpiry, secondsUntilExpiry * 1000)
      );
    } else {
      // Guides / customers: silent expiry only, no modal
      timers.push(
        setTimeout(handleExpiry, secondsUntilExpiry * 1000)
      );
    }

    return () => timers.forEach(clearTimeout);
    // accessToken is the only dependency — re-run whenever a new token is issued
  }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleExpiry() {
    setShowWarning(false);
    clearTokens();
    redirectToLogin();
    window.location.replace('/login');
  }

  const payload = accessToken ? decodeJwtPayload(accessToken) : null;
  const userCategory = resolveUserCategory(payload);

  return {
    showWarning,
    userCategory,
  };
}
